import React, { useRef, useState } from "react";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import CheckIcon from "@mui/icons-material/Check";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { TooltipButton } from "components/common/TooltipButton";
import { MenuItem } from "components/common/MenuItem";
import { DropdownMenu } from "components/common/DropdownMenu";

interface PendingAccessControlsProps {
  email: string;
  canRevokeAccess: boolean;
  onResend?: () => Promise<void>;
  onRevoke?: () => Promise<void>;
  onMenuToggle?: (isOpen: boolean) => void;
}

export const PendingAccessControls: React.FC<PendingAccessControlsProps> = ({
  canRevokeAccess,
  onResend,
  onRevoke,
  onMenuToggle,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  const isBusy = isResending || isRevoking;

  const handleMenuOpen = () => {
    if (isBusy) return;
    setMenuOpen(true);
    onMenuToggle?.(true);
  };

  const handleMenuClose = () => {
    setMenuOpen(false);
    onMenuToggle?.(false);
  };

  const handleResend = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onResend || isResending) return;
    setIsResending(true);
    handleMenuClose();
    try {
      await onResend();
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 2000);
    } finally {
      setIsResending(false);
    }
  };

  const handleRevoke = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRevoke || isRevoking) return;
    setIsRevoking(true);
    handleMenuClose();
    try {
      await onRevoke();
    } finally {
      setIsRevoking(false);
    }
  };

  const hasActions = !!onResend || (canRevokeAccess && !!onRevoke);

  if (!hasActions) {
    return (
      <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
        <span className="text-fg-muted-medium shrink-0">Invited</span>
        <div className="flex-none w-5 h-5" />
      </div>
    );
  }

  return (
    <div className="flex items-center shrink-0 gap-1 min-w-26 justify-end">
      {resendSuccess && (
        <span className="flex items-center justify-center w-3 h-3 rounded-full border border-success mr-1">
          <CheckIcon className="text-success" style={{ fontSize: 6 }} />
        </span>
      )}
      <span className="text-fg-muted-regular">Invited</span>
      <div
        className={`flex-none transition-opacity duration-200 ${
          isMenuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        } ${isBusy ? "opacity-40 pointer-events-none" : ""}`}
      >
        <TooltipButton
          ref={triggerRef}
          title="Actions"
          disableTooltip={isMenuOpen}
          variant="contextual"
          size="xs"
          onClick={isMenuOpen ? handleMenuClose : handleMenuOpen}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen}
          className={isMenuOpen ? "button-active-state" : ""}
        >
          <MoreVertIcon />
        </TooltipButton>
      </div>

      <DropdownMenu
        anchorRef={triggerRef}
        isOpen={isMenuOpen}
        onClose={handleMenuClose}
        label="Actions"
        placement="bottom-end"
      >
        {onResend && (
          <MenuItem
            Icon={SendOutlinedIcon}
            label="Resend Invitation"
            onClick={handleResend}
            disabled={isBusy}
          />
        )}
        {canRevokeAccess && onRevoke && (
          <MenuItem
            Icon={PersonRemoveOutlinedIcon}
            label="Cancel Invite"
            onClick={handleRevoke}
            disabled={isBusy}
          />
        )}
      </DropdownMenu>
    </div>
  );
};
